import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { EmptyState, Page, PageHeader } from "../../../components/ui";
import { getAdDetails, setAdMediaId, updateAd } from "../../../services/ads";
import { jwplayerRequest, uploadFileToLink } from "../../../services/jwplayer";
import AdForm, { adFormValues, adRequestBody } from "./AdForm";

/**
 * Creates the JW Player media, uploads `file` to it and, once uploaded,
 * links it to the ad. Resolves with the updated ad, or `null` on failure.
 */
export async function uploadAdVideo(ad, file, { title, description }) {
  const created = await jwplayerRequest({
    body: {
      upload: { method: "direct", mime_type: "video/mp4" },
      metadata: { title, description },
    },
    url: "/media/",
    method: "post",
    jw_tag: false,
  });
  const { id, upload_link } = created.data.data;
  const uploaded = await uploadFileToLink(upload_link, file, file.type);
  if (!uploaded) return null;
  const data = await setAdMediaId(ad._id, id);
  return data?.savedAds || null;
}

const AdVideo = ({ ad, file, onFileChange, onUploaded }) => {
  const { t } = useTranslation();
  const [replacing, setReplacing] = useState(false);
  const [uploading, setUploading] = useState(false);

  const upload = async () => {
    setUploading(true);
    try {
      const updated = await uploadAdVideo(ad, file, ad);
      if (updated) {
        toast.success(t("Video Uploaded Successfully!"));
        onUploaded(updated);
        setReplacing(false);
      } else {
        toast.error(t("Video Uploading Failed!"));
      }
    } catch (error) {
      console.error(error);
      toast.error(t("Video Uploading Failed!"));
    } finally {
      setUploading(false);
    }
  };

  const showPlayer = ad.media_id && !replacing;
  return (
    <section className="ui-card ad-video">
      <h2 className="ad-video-title">{t("Video")}</h2>
      {showPlayer ? (
        <div className="ad-video-player">
          <iframe
            src={`https://cdn.jwplayer.com/players/${ad.media_id}-4t00MwmP.html`}
            frameBorder="0"
            allowFullScreen
            title={t("Video")}
          />
        </div>
      ) : (
        <label className="ad-dropzone" htmlFor="ad-video">
          <i className="ri-upload-cloud-line" aria-hidden="true"></i>
          <strong>{file ? file.name : t("upload video")}</strong>
          <span className="ui-hint">MP4</span>
          <input
            id="ad-video"
            type="file"
            accept="video/mp4,video/x-m4v,video/*"
            className="sr-only"
            disabled={uploading}
            onChange={(e) => onFileChange(e.target.files && e.target.files[0])}
          />
        </label>
      )}
      <div className="ad-video-actions">
        {showPlayer ? (
          <button
            type="button"
            className="ui-btn ui-btn-secondary"
            onClick={() => setReplacing(true)}
          >
            <i className="ri-refresh-line" aria-hidden="true"></i>
            {t("Reupload")}
          </button>
        ) : (
          <>
            {replacing && (
              <button
                type="button"
                className="ui-btn ui-btn-secondary"
                disabled={uploading}
                onClick={() => {
                  setReplacing(false);
                  onFileChange(null);
                }}
              >
                {t("cancel")}
              </button>
            )}
            <button
              type="button"
              className="ui-btn"
              disabled={!file || uploading}
              onClick={upload}
            >
              {uploading && <span className="ad-spinner" aria-hidden="true" />}
              {uploading ? t("Uploading...") : t("Upload")}
            </button>
          </>
        )}
      </div>
    </section>
  );
};

const UpdateAds = () => {
  const { ad_id } = useParams();
  const { t } = useTranslation();
  const [ad, setAd] = useState(null);
  const [loading, setLoading] = useState(true);
  const [videoFile, setVideoFile] = useState(null);

  useEffect(() => {
    getAdDetails(ad_id)
      .then((data) => setAd(data?.savedAds || null))
      .catch((error) => {
        console.error("API Error:", error);
        toast.error(t("Error fetching ad data."));
      })
      .finally(() => setLoading(false));
  }, [ad_id, t]);

  const handleSubmit = async (values) => {
    try {
      await updateAd(ad_id, adRequestBody(values));
      toast.success(t("Ad updated successfully!"));
    } catch (error) {
      console.error("API Error:", error);
      toast.error(t("Error updating ad."));
    }
  };

  return (
    <Page>
      <PageHeader title={t("Update Ad")} subtitle={ad?.title} />
      {loading ? (
        <div className="ui-skeleton" style={{ height: 480 }} />
      ) : !ad ? (
        <section className="ui-card">
          <EmptyState>{t("No Data Found")}</EmptyState>
        </section>
      ) : (
        <div className="ad-layout">
          <AdForm
            edit
            initialValues={adFormValues(ad)}
            hasMedia={Boolean(ad.media_id || videoFile)}
            onSubmit={handleSubmit}
            submitLabel={t("Update")}
          />
          <AdVideo
            ad={ad}
            file={videoFile}
            onFileChange={setVideoFile}
            onUploaded={(updated) => {
              setAd(updated);
              setVideoFile(null);
            }}
          />
        </div>
      )}
    </Page>
  );
};

export default React.memo(UpdateAds);
